import React from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { useAuth } from "../../context/AuthContext";
import { normalizeProjectModules } from "../../utils/projectModules";
import { billingAccessActionLabel, billingAccessLabel, getProjectBillingAccess } from "../../utils/projectBillingAccess";
import { resolveProjectNavigation } from "../../utils/dashboardNavigation";
import { isSuperAdmin } from "../../utils/roles";
import MobileProjectSettingsLayout from "./mobile/MobileProjectSettingsLayout";
import { getProjectSettingsSection } from "./projectSettingsSections";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function currentSection(pathname) {
  return getProjectSettingsSection(pathname);
}

export default function ProjectSettingsLayout() {
  const { projectId } = useParams();
  const mobile = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [readiness, setReadiness] = React.useState(null);

  const project = React.useMemo(() => {
    const matched = projects.find(
      (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
    );
    const activeMatches =
      activeProject?.slug === projectId ||
      activeProject?.id === projectId ||
      activeProject?._id === projectId;
    return matched || (activeMatches ? activeProject : null);
  }, [projects, projectId, activeProject]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    if (project && project.slug !== activeProject?.slug) setActiveProject(project);
  }, [project, activeProject?.slug, setActiveProject]);

  React.useEffect(() => {
    let cancelled = false;
    const fallback = project?.readiness || null;
    setReadiness(fallback);

    if (!projectId) return () => { cancelled = true; };

    projectsApi
      .getReadiness(projectId)
      .then((data) => {
        if (!cancelled) setReadiness(data?.readiness || fallback);
      })
      .catch(() => {
        if (!cancelled) setReadiness(fallback);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, project?.readiness?.computedAt]);

  const modules = normalizeProjectModules(project?.modules);
  const activeModules = Object.values(modules).filter(Boolean).length;
  const billingAccess = getProjectBillingAccess(project);
  const billingChipColor = billingAccess.isFull
    ? "success"
    : billingAccess.isGrace
      ? "warning"
      : "error";
  const subscriptionLabel = billingAccessLabel(billingAccess);
  const subscriptionNeedsAttention = billingAccess.needsPaymentAction;
  const section = currentSection(location.pathname);
  const settingsNavigation = React.useMemo(
    () => resolveProjectNavigation({
      project,
      memberAccess: project?.memberAccess,
      superAdmin: isSuperAdmin(user),
      pathname: location.pathname,
    }).toolsByKey.settings,
    [project, user, location.pathname],
  );
  const settingsAccessByValue = React.useMemo(
    () => Object.fromEntries(
      (settingsNavigation?.secondaryTools || []).map((tool) => [
        String(tool.key || "").replace(/^settings-/, ""),
        tool,
      ]),
    ),
    [settingsNavigation],
  );
  const readinessFacts = readiness?.facts || {};
  const readinessKnown = Boolean(readiness && typeof readiness === "object");
  const identityReady = readinessFacts.identity
    ? readinessFacts.identity.complete === true
    : Boolean(project?.name && project?.slug);
  const businessDataReady = identityReady && Boolean(project?.businessProfile);

  const attentionItems = React.useMemo(() => {
    const items = [];

    if (readinessKnown && !businessDataReady) {
      items.push({
        key: "business",
        sectionValue: "general",
        title: "Completar datos del negocio",
        description: "Faltan datos esenciales para dejar bien identificado el proyecto.",
        actionLabel: "Revisar negocio",
        icon: <SettingsRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=business`,
        tone: "warning",
      });
    }

    if (subscriptionNeedsAttention) {
      items.push({
        key: "subscription",
        sectionValue: "suscripcion",
        title: "El plan requiere atención",
        description: subscriptionLabel,
        actionLabel: billingAccessActionLabel(billingAccess),
        icon: <WorkspacePremiumRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/suscripcion`,
        tone: billingAccess.isSuspended ? "error" : "warning",
      });
    }

    return items.filter((item) => settingsAccessByValue[item.sectionValue]?.visible !== false);
  }, [
    billingAccess,
    businessDataReady,
    projectId,
    readinessKnown,
    settingsAccessByValue,
    subscriptionLabel,
    subscriptionNeedsAttention,
  ]);

  // La sección abierta es la autoridad visual de sus propios problemas. El shell
  // sólo eleva pendientes transversales para evitar repetir, por ejemplo, un
  // estado de billing inmediatamente antes de la propia pantalla de Plan.
  const visibleAttentionItems = React.useMemo(
    () => attentionItems.filter((item) => item.sectionValue !== section),
    [attentionItems, section],
  );

  const openArea = (area) => navigate(area.path);

  // Cobros online y Notificaciones conservan sus rutas legacy dentro de
  // /configuracion, pero conceptualmente pertenecen a Ventas y Más. Evitamos
  // envolverlas con el encabezado, tarjetas y pestañas de Configuración para que
  // la navegación nueva sea la que marque su contexto real.
  if (["ecommerce", "notificaciones"].includes(section)) {
    return <Outlet />;
  }

  if (mobile) {
    return (
      <MobileProjectSettingsLayout
        project={project}
        activeModules={activeModules}
        attentionItems={visibleAttentionItems}
        onAttentionOpen={openArea}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Configuración
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            {project?.name || projectId} · cerrá lo esencial del proyecto y volvé acá para mantenimiento
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            icon={<WorkspacePremiumRoundedIcon />}
            label={`${activeModules} módulos activos`}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
          <Chip
            label={billingAccessLabel(billingAccess)}
            color={billingChipColor}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
        </Stack>
      </Stack>

      {visibleAttentionItems.length ? (
        <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                  Requiere atención
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                  Sólo mostramos configuraciones que necesitan una acción concreta.
                </Typography>
              </Box>
              <Chip
                label={`${visibleAttentionItems.length} ${visibleAttentionItems.length === 1 ? "pendiente" : "pendientes"}`}
                color="warning"
                variant="outlined"
                sx={{ alignSelf: "flex-start", fontWeight: 900 }}
              />
            </Stack>

            <Grid container spacing={1.25}>
              {visibleAttentionItems.map((item) => (
                <Grid item xs={12} md={6} key={item.key}>
                  <ButtonBase
                    onClick={() => openArea(item)}
                    aria-label={`${item.actionLabel}: ${item.title}`}
                    sx={{
                      width: "100%",
                      height: "100%",
                      alignItems: "stretch",
                      textAlign: "left",
                      borderRadius: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        p: 1.55,
                        borderRadius: 3,
                        border: "1px solid rgba(25,36,47,.08)",
                        bgcolor: item.tone === "error" ? "rgba(211,47,47,.035)" : "rgba(237,108,2,.035)",
                      }}
                    >
                      <Stack direction="row" spacing={1.15} alignItems="flex-start">
                        <Box sx={{ mt: .15, color: item.tone === "error" ? "error.main" : "warning.main" }}>
                          {item.icon}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", mt: .35, color: "rgba(25,36,47,.58)", lineHeight: 1.35 }}>
                            {item.description}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", mt: .65, color: BRAND.orange, fontWeight: 950 }}>
                            {item.actionLabel}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </ButtonBase>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      <Card
        elevation={0}
        sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", overflow: "hidden" }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3.5 }, bgcolor: "#FBFBFD" }}>
          <Outlet />
        </CardContent>
      </Card>
    </Stack>
  );
}
